import { faSolidPencilCirclePlus } from '@awesome.me/kit-09b03e5df4/icons/kit/custom';
import { css } from '@emotion/react';
import {
  faChartColumn, faCircleDot, faCog, faDiamondTurnRight, faDrawPolygon, faEdit, faEllipsisVertical, faExternalLink,
  faFileChartColumn, faFilterList, faGlobe, faGlobePointer, faScissors,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type MutableRefObject, useMemo, useRef,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import {
  TinyMapColorTileComponent, type TinyMapColorTileComponentProps,
} from '~/_shared/baseComponents/tinyMapPlaceholder';
import {
  ToggleComponent, ToggleSize,
} from '~/_shared/baseComponents/toggle/toggle.component';
import { HintTooltipComponent } from '~/_shared/baseComponents/tooltip/hintTooltip.component';
import { TooltipPlacement } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { LimitNumberOfLinesComponent } from '~/_shared/components/limitNumberOfLines/limitNumberOfLines.component';
import { MetricsListComponent } from '~/_shared/components/metricsList/metricsList.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import type { DisabledState } from '~/_shared/types/disabledState';
import { useTranslation } from '~/_shared/utils/hooks';
import { type MetricsResults } from '~/_shared/utils/metric/useMetricData.helpers';
import { ISOLATE_BOUNDARY_MAXIMUM_NUMBER_OF_POLYGONS } from '~/boundary/boundary.constants';
import { MetricsPanel } from '~/proximityDetails/metricsPanel/metricsPanel';
import { MapToolHeaderButtonComponent } from '~/sidebar/sidebarApps/mapToolWrapper/mapTooHeaderButton.component';
import { SidebarTitleComponent } from '~/sidebar/sidebarApps/rightSidebar/sidebarTitle/sidebarTitle.component';
import type { BoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { type OptimizedTerritoriesBlockerFn } from '../../mapTools/boundary/activeBoundary/boundaryItem/useOptimizedTerritoriesFeatureBlocker';
import { getTotalLocationsStyle } from '../helpers/getTotalLocationsStyle';
import { type SidebarRoundMenuButtonProps } from '../roundButtonMenu/sidebarRoundButtonMenu.types';
import { SidebarContentContainer } from '../sidebarContent/sidebarContent.container';
import { type ContextMenuItemProps } from '../sidebarContextMenu/sidebarContextMenu.types';
import { IsolateBoundaryColorPickerComponent } from './isolateBoundaryColorPicker.component';

const locationDescriptionStyle = css({
  paddingLeft: '8px',
});

const alertWrapperStyle = css({
  fontSize: 14,
});

const alertHeadingStyle = css({
  fontWeight: 700,
});

const customizeMetricsWrapperStyle = css({
  background: 'inherit',
});

//TODO: Replace affected div with <Stack> once it makes its way to master.
const stackStyles = css({
  padding: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const syntheticBoundaryNoteStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  textTransform: 'initial',
  fontSize: 14,
  lineHeight: '16px',
  alignItems: 'center',
});

const entireGroupToggleStyle = css({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  width: '100%',
  padding: '0 12px 0 0',
});

const entireGroupToggleLabelStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
});

const entireGroupToggleCaptionStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  fontSize: 10,
  lineHeight: 1.1,
  textTransform: 'uppercase',
});

const entireGroupToggleValueStyle = (theme: Theme) => css({
  color: theme.textColors.primary,
  fontSize: 12,
  lineHeight: 1.1,
  textTransform: 'capitalize',
});

type BoundaryDetailsComponentProps = {
  boundaryName?: string;
  isLoading: boolean;
  metrics: MetricsResults;
  ignoreFilters: boolean;
  indicatorStyles?: Pick<TinyMapColorTileComponentProps, 'lineColor' | 'lineWidth' | 'color'>;
  isArtificial: boolean;
  blockWhenOptimizedTerritoriesTrial?: OptimizedTerritoriesBlockerFn;
  canShowEntireGroupDetails?: boolean;
  isShowingEntireGroupDetails?: boolean;

  onClose: () => void;
  onCustomizeMetricsClick?: () => void;
  onChangeShowEntireGroupDetails?: (isShowingEntireGroup: boolean) => void;
} & Omit<CreateButtonsParams, 'buttonRefs' | 'blockWhenOptimizedTerritoriesTrial'>;

type CreateButtonsParams = {
  boundaryPolygonsTotal: number;
  buttonRefs: {
    additionalActionsRef: MutableRefObject<HTMLButtonElement | null>;
  };
  isBoundaryIsolated?: boolean;
  boundaryPermissions: BoundaryGroupPermissions;
  isMobileVersion: boolean;
  isEditModeDisabled: boolean;
  boundaryIsOptimizedTerritoriesTrial?: boolean;
  blockWhenOptimizedTerritoriesTrial: OptimizedTerritoriesBlockerFn;
  isLayeredMapConnected: boolean;
  isEditingBoundary?: boolean;
  markersCount: number | null;
  summaryDisabled?: DisabledState;

  onAdvanceEditClick?: () => void;
  onBatchEditLocationsClick?: () => void;
  onBoundaryItemRemoveClick?: () => void;
  onBoundaryItemSettingsClick?: () => void;
  onDirectionsClick?: () => void;
  onEditBoundaryPolygonClick?: () => void;
  onExportLocationsClick?: () => void;
  onIsolateBoundaryClick?: () => void;
  onExportContainedBoundariesClick?: () => void;
  onViewSummaryClick?: () => void;
  onPreviewSpreadsheetData?: () => void;
  onShowWeightedCenter?: () => void;
};

const useCreateButtons = (params: CreateButtonsParams) => {
  const [t] = useTranslation();
  const theme = useTheme();

  const { buttonRefs, boundaryPermissions: { boundaryIsCustom, boundaryIsEditable } } = params;

  const buttonsConfiguration: SidebarRoundMenuButtonProps<string>[] = [];

  if (params.onBoundaryItemSettingsClick) {
    const text = boundaryIsCustom ? t('Territory Settings') : t('Boundary Settings');
    const openSettingsButtonConfiguration = {
      id: 'settings',
      buttonName: text,
      buttonStyle: RoundButtonStyle.Primary,
      icon: faCog,
      onClick: params.onBoundaryItemSettingsClick,
      tooltipLabel: text,
    };
    buttonsConfiguration.push(openSettingsButtonConfiguration);
  }

  const boundaryType = boundaryIsCustom ? t('Territory') : t('Boundary');

  const tooManyPolygonsForIsolateBoundary = params.boundaryPolygonsTotal > ISOLATE_BOUNDARY_MAXIMUM_NUMBER_OF_POLYGONS;
  if (params.onIsolateBoundaryClick) {
    const text = t('Isolate {{boundaryType}}', { boundaryType });
    const isolateBoundaryButtonConfiguration = {
      id: 'isolate',
      buttonName: text,
      buttonStyle: tooManyPolygonsForIsolateBoundary ? RoundButtonStyle.Disabled :
        params.isBoundaryIsolated ? RoundButtonStyle.Quaternary : RoundButtonStyle.Primary,
      icon: faScissors,
      onClick: params.onIsolateBoundaryClick,
      tooltipLabel: tooManyPolygonsForIsolateBoundary
        ? t('Isolate {{boundaryType}} tooManyPolygons', { boundaryType }) : text,
      notSelectable: tooManyPolygonsForIsolateBoundary,
    };
    buttonsConfiguration.push(isolateBoundaryButtonConfiguration);
  }

  if (params.onEditBoundaryPolygonClick) {
    const text = t('Edit {{boundaryType}}', { boundaryType });
    const editBoundaryButtonStyle = !params.isEditingBoundary ? params.boundaryIsOptimizedTerritoriesTrial ? RoundButtonStyle.Restricted : RoundButtonStyle.Primary : RoundButtonStyle.ConfirmInverted;

    const editPolygonButtonConfiguration = {
      id: 'editPolygon',
      buttonName: text,
      buttonStyle: editBoundaryButtonStyle,
      icon: faDrawPolygon,
      onClick: params.blockWhenOptimizedTerritoriesTrial(params.onEditBoundaryPolygonClick),
      tooltipLabel: text,
    };

    buttonsConfiguration.push(editPolygonButtonConfiguration);
  }
  else if (boundaryIsCustom && !boundaryIsEditable && !params.isMobileVersion && !params.isEditModeDisabled) {
    const text = t('The owner of the territory ({{ownerName}}) doesn\'t allow editing of this territory group',
      { ownerName: params.boundaryPermissions.boundaryOwnerName });
    const editPolygonButtonConfiguration = {
      id: 'editPolygon',
      buttonName: text,
      buttonStyle: RoundButtonStyle.Disabled,
      icon: faDrawPolygon,
      tooltipLabel: text,
      notSelectable: true,
    };

    buttonsConfiguration.push(editPolygonButtonConfiguration);
  }

  if (params.onAdvanceEditClick) {
    const text = t('Advanced Add / Edit {{boundaryType}}', { boundaryType });
    const advanceEditButtonConfiguration = {
      id: 'advancedEdit',
      buttonName: text,
      buttonStyle: params.boundaryIsOptimizedTerritoriesTrial ? RoundButtonStyle.Restricted : RoundButtonStyle.Primary,
      icon: faSolidPencilCirclePlus,
      onClick: params.blockWhenOptimizedTerritoriesTrial(params.onAdvanceEditClick),
      tooltipLabel: text,
    };

    buttonsConfiguration.push(advanceEditButtonConfiguration);
  }

  if (params.onViewSummaryClick && !boundaryIsCustom) {
    let label = t('View {{boundaryType}} Summary', { boundaryType });
    if (params.summaryDisabled?.isDisabled) {
      label = params.summaryDisabled.reason;
    }

    const isDisabled = !!params.summaryDisabled?.isDisabled;

    buttonsConfiguration.push({
      id: 'viewSummary',
      buttonName: label,
      icon: faFileChartColumn,
      tooltipLabel: label,
      onClick: params.onViewSummaryClick,
      buttonStyle: isDisabled ? RoundButtonStyle.Disabled : RoundButtonStyle.Primary,
      notSelectable: isDisabled,
    });
  }

  const iconStyle = params.boundaryIsOptimizedTerritoriesTrial ? css({ color: theme.textColors.warning }) : undefined;

  const hasMarkers = (params.markersCount || 0) > 0;

  const additionalButtons: ContextMenuItemProps[] = [
    ...(params.onViewSummaryClick && boundaryIsCustom && !params.summaryDisabled?.isDisabled ? [{ text: t('View {{boundaryType}} Summary', { boundaryType }), icon: faFileChartColumn, onClick: params.onViewSummaryClick }] : []),
    ...(params.onPreviewSpreadsheetData ? [{ text: t('View {{entityName}} Data', { entityName: boundaryType }), icon: faFilterList, onClick: params.onPreviewSpreadsheetData }] : []),
    ...(params.onBatchEditLocationsClick && hasMarkers && !params.isLayeredMapConnected ? [{ text: t('Batch Edit Locations'), icon: faEdit, iconStyle, onClick: params.blockWhenOptimizedTerritoriesTrial(params.onBatchEditLocationsClick) }] : []),
    ...(params.onExportLocationsClick && hasMarkers ? [{ text: t('Export Locations'), icon: faExternalLink, iconStyle, onClick: params.blockWhenOptimizedTerritoriesTrial(params.onExportLocationsClick) }] : []),
    ...(params.onExportContainedBoundariesClick ? [{ text: t('Export Contained Boundaries'), icon: faExternalLink, iconStyle, onClick: params.blockWhenOptimizedTerritoriesTrial(params.onExportContainedBoundariesClick) }] : []),
    ...(params.onDirectionsClick && hasMarkers ? [{ text: t('Optimize Driving Route'), icon: faDiamondTurnRight, iconStyle, onClick: params.blockWhenOptimizedTerritoriesTrial(params.onDirectionsClick) }] : []),
    ...(params.onShowWeightedCenter && hasMarkers ? [{ text: 'Show Weighted Center', icon: faCircleDot, iconStyle, onClick: params.blockWhenOptimizedTerritoriesTrial(params.onShowWeightedCenter) }] : []),
  ];

  if (additionalButtons.length > 0) {
    const additionalActionsButton: SidebarRoundMenuButtonProps<string> = {
      id: 'additionalActions',
      buttonName: t('Additional Actions'),
      buttonStyle: params.boundaryIsOptimizedTerritoriesTrial ? RoundButtonStyle.Restricted : RoundButtonStyle.Primary,
      contextItemMenuStructure: additionalButtons,
      icon: faEllipsisVertical,
      tooltipLabel: t('Additional Actions'),
      ref: buttonRefs.additionalActionsRef,
    };

    buttonsConfiguration.push(additionalActionsButton);
  }

  if (params.onBoundaryItemRemoveClick) {
    const deleteButtonConfiguration = {
      id: 'delete',
      buttonName: t('Delete'),
      buttonStyle: RoundButtonStyle.Danger,
      icon: faTrash,
      onClick: params.onBoundaryItemRemoveClick,
      tooltipLabel: t('Delete'),
    };

    buttonsConfiguration.push(deleteButtonConfiguration);
  }

  return buttonsConfiguration;
};

export const BoundaryDetailsComponent: FC<BoundaryDetailsComponentProps> = (props) => {
  const [t] = useTranslation();
  const theme = useTheme();
  const additionalActionsRef = useRef<HTMLButtonElement | null>(null);
  const blockWhenOptimizedTerritoriesTrial = props.blockWhenOptimizedTerritoriesTrial ?? (fn => () => fn());
  const buttons = useCreateButtons({ buttonRefs: { additionalActionsRef }, ...props, blockWhenOptimizedTerritoriesTrial });
  const ignoreFiltersAlert = (
    <div css={alertWrapperStyle}>
      <AlertComponent
        size="small"
        type="warning"
        stylingOptions={{ transparentBackground: true }}
        collapsedContent={(<span css={alertHeadingStyle}>{t('Ignore Filters is activated.')}</span>)}
      >
        <span css={alertHeadingStyle}>{t('Ignore Filters is activated.')}</span>
        <p>
          {t('The counts and metrics calculated in this panel might include markers which are not visible on the map.')}
        </p>
      </AlertComponent>
    </div>
  );

  const boundaryIndicator = useMemo(() => {
    if (!props.indicatorStyles) {
      return undefined;
    }

    return (
      <TinyMapColorTileComponent
        color={props.indicatorStyles.color}
        lineColor={props.indicatorStyles.lineColor}
        lineWidth={props.indicatorStyles.lineWidth}
        fill
        outline
      />
    );
  }, [props.indicatorStyles]);

  return (
    <SidebarContentContainer
      body={(
        <MetricsPanel>
          <MetricsListComponent
            metrics={props.metrics}
            boundaryIsOptimizedTerritoriesTrial={props.boundaryIsOptimizedTerritoriesTrial}
          />
        </MetricsPanel>
      )}
      secondaryHeader={(props.isBoundaryIsolated || props.onCustomizeMetricsClick) ? (
        <div css={stackStyles}>
          {props.isBoundaryIsolated && <IsolateBoundaryColorPickerComponent />}
          {props.onCustomizeMetricsClick && (
            <MapToolHeaderButtonComponent
              css={customizeMetricsWrapperStyle}
              text={t('Customize Metrics')}
              prefixIcon={faChartColumn}
              prefixIconStyle={props.boundaryIsOptimizedTerritoriesTrial ? css({ color: theme.textColors.warning }) : undefined}
              onClick={blockWhenOptimizedTerritoriesTrial(props.onCustomizeMetricsClick)}
            />
          )}
          {!props.isLoading && props.ignoreFilters ? ignoreFiltersAlert : undefined}
        </div>
      ) : null}
      isLoading={props.isLoading}
      description={(
        <SidebarTitleComponent
          containerStyle={locationDescriptionStyle}
          onEditClick={props.isMobileVersion ? undefined : props.onBoundaryItemSettingsClick}
          onXClick={props.onClose}
          sidebarTitle={{
            semiHeader: props.isArtificial ? (
              <Inline
                gap={8}
                css={syntheticBoundaryNoteStyle}
              >
                <LimitNumberOfLinesComponent
                  maxNumberOfLines={1}
                >
                  <div>{t('SyntheticBoundary.Note')}</div>
                </LimitNumberOfLinesComponent>
                <HintTooltipComponent
                  tooltipContent={t('SyntheticBoundary.Tooltip')}
                  placement={TooltipPlacement.Left}
                />
              </Inline>
            ) : undefined,
            header: props.boundaryName,
            description: props.markersCount === null ? undefined : [t('Total Locations: {{locationsCount}}', {
              locationsCount: props.markersCount,
            })],
          }}
          indicator={boundaryIndicator}
          sidebarTitleStyle={getTotalLocationsStyle(theme)}
          contentAfter={props.canShowEntireGroupDetails && props.onChangeShowEntireGroupDetails ? [
            <div
              key="entireGroupToggle"
              css={entireGroupToggleStyle}
              aria-label={t('boundaryDetails.showingDetailsFor')}
            >
              <div css={entireGroupToggleLabelStyle}>
                <span css={entireGroupToggleCaptionStyle}>{t('boundaryDetails.showingDetailsFor')}</span>
                <span css={entireGroupToggleValueStyle}>
                  {props.isShowingEntireGroupDetails
                    ? t('boundaryDetails.entireGroup')
                    : t('boundaryDetails.thisTerritory')}
                </span>
              </div>
              <ToggleComponent
                size={ToggleSize.M}
                isOn={!props.isShowingEntireGroupDetails}
                onIcon={faGlobePointer}
                offIcon={faGlobe}
                onChange={(showOnlyCurrentBoundary) => {
                  props.onChangeShowEntireGroupDetails?.(!showOnlyCurrentBoundary);
                }}
              />
            </div>,
          ] : undefined}
        />
      )}
      headerProps={{
        currentLocationIndex: 0,
        totalLocations: 1,
      }}
      menuProps={{
        roundButtonRightDetails: buttons,
        roundButtonLeftDetails: undefined,
        selectedButton: null,
      }}
    />
  );
};
